import { useMutation, useQuery, skipToken } from '@apollo/client/react'
import { gql, type DocumentNode } from '@apollo/client'
import type { ResultOf, VariablesOf, TypedDocumentNode } from '@graphql-typed-document-node/core'

import { useMemo, useState } from 'react'
import { builder } from './formBuilder'
import { get } from 'lodash'

interface Props<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  MutationDoc extends TypedDocumentNode<any, any>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  QueryDoc extends TypedDocumentNode<any, any>,
  QueryKey extends keyof ResultOf<QueryDoc>,
  Attributes,
> {
  id?: string
  existingRecord?: string
  defaultValues?: Attributes
  initializer?: (node: NonNullable<ResultOf<QueryDoc>[QueryKey]>) => Attributes
  query?: QueryDoc
  mutation: MutationDoc
  queryKey?: keyof ResultOf<QueryDoc>
  queryVariables?: VariablesOf<QueryDoc>
  resultKey: keyof ResultOf<MutationDoc>
  refetchQueries?: DocumentNode[]
}

export interface ValidationError {
  path: string
  message: string
}

const NOOP_QUERY = gql`query Noop { __typename }`

export const useForm = <
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  MutationDoc extends TypedDocumentNode<any, any>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  QueryDoc extends TypedDocumentNode<any, any>,
  QueryKey extends keyof ResultOf<QueryDoc>,
  Attributes extends VariablesOf<MutationDoc>,
>({
  id,
  defaultValues,
  initializer,
  query,
  mutation,
  queryKey,
  queryVariables,
  resultKey,
  refetchQueries = []
}: Props<MutationDoc, QueryDoc, QueryKey, Attributes>) => {
  const { data, loading: initQueryLoading, error: initQueryError } = useQuery(
    query ?? NOOP_QUERY as QueryDoc,
    query ? { variables: queryVariables || { filters: { idEq: id } } } : skipToken
  )

  const [initializerCallback] = useState(() => initializer ?? (() => ({} as Attributes)))
  const [edits, setEdits] = useState<Partial<Attributes>>({})

  const [mutate] = useMutation(mutation, { refetchQueries })

  const [mutationLoading, setMutationLoading] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState<boolean>() // undef: no state, false: failed, true: succeeded
  const [fieldErrors, setFieldErrors] = useState<Record<keyof Attributes, string>>()
  const [mutationError, setMutationError] = useState<string>()
  const [mutationData, setMutationData] = useState<ResultOf<MutationDoc>>()

  const baseAttributes = useMemo(() => {
    const record = queryKey ? data?.[queryKey]?.nodes?.[0] : undefined
    return record ? initializerCallback(record) : defaultValues
  }, [data, queryKey, initializerCallback, defaultValues])

  const attributes = { ...baseAttributes, ...edits } as Attributes
  const setAttribute = <K extends keyof Attributes>(key: K, value: Attributes[K]) => setEdits(prev => ({ ...prev, [key]: value }))

  const formBuilder = builder<Attributes>({ attributes, setAttribute, mutationError, fieldErrors })

  const save = async () => {
    setMutationLoading(true)
    try {
      const response = await mutate({ variables: { id, ...attributes } })

      const error = response.error?.message
      const validationErrors = {} as Record<keyof Attributes, string>
      (get(response.data, resultKey)?.errors as Array<ValidationError> || [])
        .forEach((e: ValidationError) => validationErrors[e.path as keyof Attributes] = e.message)

      if (!mutationError && Object.keys(validationErrors).length == 0) {
        setMutationData(response.data)
        setSaveSuccess(true)
      } else {
        setSaveSuccess(false)
      }

      setTimeout(() => {
        setSaveSuccess(undefined)
      }, 1500)

      setFieldErrors(validationErrors)
      setMutationError(error)
    } catch (e) {
      if (typeof e === 'string') {
        setMutationError(e)
      } else if (e instanceof Error) {
        setMutationError(e.message)
      }
    } finally {
      setMutationLoading(false)
    }
  }

  return {
    attributes,
    setAttribute,
    save,
    saveSuccess,
    formBuilder,
    fieldErrors,
    queryError: initQueryError?.message || undefined,
    queryLoading: initQueryLoading || false,
    queryData: data,
    mutationLoading,
    mutationData,
  }
}
